import {useEffect, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {getTimetableSales} from '../../../../../redux/realtime/realtimeActions';
import {getTimetable} from '../../../../../redux/timetable/timetableActions';
import setGlobalState from '../../../../../redux/setGlobalState';
import {getDateFormat, getHourMinutes} from '../../../../../library/dates';
import {XHR_LOADING_STATE, XHR_SUCCESS_STATE, XHR_FAILED_STATE} from '../../../../../assets/constants/constants';
import {TTimetableSales, TTimetableSalesLecture} from '../../../../../redux/realtime/realtime.type';
import {useTimetableDays} from '../../../../timetable/hooks/useTimetableDays';

export type TTimeGroup = [string, TTimetableSalesLecture[]];

export const isTimetableSalesPending = (data: TTimetableSales | null, xhr: string) =>
    xhr === XHR_LOADING_STATE || (!data && xhr !== XHR_FAILED_STATE);

export function useTimetableSalesData(eid: number) {
    const data: TTimetableSales | null = useSelector((s: any) => s.realtime?.event?.timetableSales);
    const xhr = useSelector((s: any) => s.realtime?.event?.timetableSalesXHR);
    const showSkeleton = isTimetableSalesPending(data, xhr);
    const isTimetableLoaded = useSelector((s: any) => s.timetable?.getLecturesXHR === XHR_SUCCESS_STATE);
    const {currentDay, currentDayIndex} = useTimetableDays({controlled: true});

    useEffect(() => {
        if(eid) {
            getTimetableSales(eid);
            if(!isTimetableLoaded) getTimetable();
        }
        return () => setGlobalState('timetable.currentDayIndex', 0);
    }, [eid]);

    const selectedDayKey = currentDay ? getDateFormat(currentDay.timestamp, 'YYYY-MM-DD') : null;
    const timeGroups = useMemo<TTimeGroup[]>(() => {
        const all = data?.lectures || [];
        const dayLectures = currentDayIndex === -1
            ? all.filter(l => !l.time)
            : (selectedDayKey ? all.filter(l => l.time && getDateFormat(l.time, 'YYYY-MM-DD') === selectedDayKey) : all);
        const map = new Map<string, TTimetableSalesLecture[]>();
        dayLectures.forEach(l => {
            const key = l.time ? getHourMinutes(l.time) : '—';
            if(!map.has(key)) map.set(key, []);
            map.get(key).push(l);
        });
        return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    }, [data, selectedDayKey, currentDayIndex]);

    const summary = useMemo(() => {
        if(!data) return null;
        const unlimitedTaken = data.lectures.reduce((sum, l) => sum + (l.capacity.amount == null ? l.capacity.taken : 0), 0);
        if(!unlimitedTaken) return data.summary;
        const {capacity} = data.summary;
        const total = capacity.total + unlimitedTaken;
        const taken = capacity.taken + unlimitedTaken;
        return {...data.summary, capacity: {...capacity, total, taken, percent: total ? Math.round((taken / total) * 100) : capacity.percent}};
    }, [data]);

    return {data, summary, showSkeleton, timeGroups};
}
